import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Pipeline',
    width: '25%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Type',
    width: '20%',
    filter: {
      type: 'aggregation',
      aggregationName: 'asset_type_aggregation',
      remoteSearchFields: ['asset_type'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'asset_type',
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    width: '10%',
    filter: {
      type: 'aggregation',
      aggregationName: 'env_aggregation',
      remoteSearchFields: ['env'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'env',
  },
  { name: 'links', label: 'Harmony URL', width: '15%' },
  {
    name: 'businessMetadata.administrativeMetadata.owners.name',
    label: 'Owners',
    width: '20%',
  },
  { name: 'lineage', label: 'Lineage', width: '10%' },
];

const attributesMap: Attribute[] = [
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    showInColumn: 0,
    description: 'Asset environment',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.harmony_url',
    label: 'Harmony URL',
    type: 'link',
    showInColumn: 0,
    description: 'Harmony URL',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.Harmony.datahub_urn',
    label: 'Datahub URN',
    showInColumn: 1,
    description: 'Datahub URN',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.Harmony.cluster',
    label: 'Cluster',
    showInColumn: 1,
    description: 'Harmony cluster',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.Harmony.tags',
    label: 'Tags',
    showInColumn: 2,
    description: 'Tags added to this asset',
    type: 'table',
    tableColumns: ['Name', 'Value'],
    tableFieldMapping: {
      Name: 'name',
      Value: 'value',
    },
  },
  {
    name: 'sourceSpecificMetadata.Harmony.scan_date',
    label: 'Scan Date',
    showInColumn: 1,
    description: 'Date when this asset was scanned',
    type: 'date',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.long_running_threshold',
    label: 'Long Running Threshold',
    showInColumn: 1,
    description: 'Long Running Threshold',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.end_date',
    label: 'End Date',
    showInColumn: 1,
    description: 'Asset end date',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.timezone',
    label: 'Timezone',
    showInColumn: 1,
    description: 'Timezone',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.ordering',
    label: 'Ordering',
    showInColumn: 1,
    description: 'Ordering',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.task_memory_in_mb',
    label: 'Task Memory',
    suffix: ' MB',
    showInColumn: 1,
    description: 'Task memory in MB',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.send_mail',
    label: 'Send Mail',
    showInColumn: 1,
    description: 'Flag to show to send email',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.last_sequential_run',
    label: 'Last Sequential Run',
    showInColumn: 1,
    description: 'Last Sequential Run',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.task_timeout_in_minutes',
    label: 'Task Timeout',
    suffix: ' minutes',
    showInColumn: 1,
    description: 'Task timeout in minutes',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.enabled',
    label: 'Enabled',
    showInColumn: 1,
    description: 'Enabled',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.max_concurrency',
    label: 'Max Concurrency',
    showInColumn: 1,
    description: 'Max Concurrency',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.delay_check_enabled',
    label: 'Delay Check Enabled',
    showInColumn: 1,
    description: 'Delay Check Enabled',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.delay_threshold',
    label: 'Delay Threshold',
    showInColumn: 1,
    description: 'Delay Threshold',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.failure_threshold',
    label: 'Failure Threshold',
    showInColumn: 1,
    description: 'Failure Threshold',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.input_data_jobs',
    label: 'Input Data Jobs',
    showInColumn: 1,
    description: 'Input Data Jobs',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.failure_check_enabled',
    label: 'Failure Check Enabled',
    showInColumn: 1,
    description: 'Failure Check Enabled',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.job_type',
    label: 'Job Type',
    showInColumn: 1,
    description: 'Job Type',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.operator_group',
    label: 'Operator Group',
    showInColumn: 1,
    description: 'Operator Group',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.email',
    label: 'Email',
    showInColumn: 1,
    description: 'Email',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.yellow_pages_service',
    label: 'Yellow Pages Service',
    showInColumn: 1,
    description: 'Yellow Pages Service',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.sla',
    label: 'SLA',
    showInColumn: 2,
    description: 'SLA',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.mail_list',
    label: 'Mail List',
    showInColumn: 1,
    description: 'Mail List',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.failure_check_time_period',
    label: 'Failure Check Time Period',
    showInColumn: 1,
    description: 'Failure Check Time Period',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.priority',
    label: 'Priority',
    showInColumn: 1,
    description: 'Priority',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.job_id',
    label: 'Job Id',
    showInColumn: 1,
    description: 'Job Id',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.max_retry',
    label: 'Max Retry',
    showInColumn: 1,
    description: 'Max Retry',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.start_date_time',
    label: 'Start Date Time',
    showInColumn: 1,
    description: 'Start Date Time',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.deleted',
    label: 'Deleted',
    showInColumn: 1,
    description: 'Deleted',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.long_running_check_enabled',
    label: 'Long Running Check Enabled',
    showInColumn: 1,
    description: 'Long Running Check Enabled',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.external_job_id',
    label: 'External Job Id',
    showInColumn: 1,
    description: 'External Job Id',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.interval',
    label: 'Interval',
    showInColumn: 1,
    description: 'Interval',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.bash',
    label: 'Bash',
    type: 'code',
    showInColumn: 1,
    description: 'Bash',
  },
  {
    name: 'sourceSpecificMetadata.Harmony.yp_service_id',
    label: 'YP Service Id',
    showInColumn: 1,
    description: 'YP Service Id',
  },
  {
    name: 'technicalMetadata.operationalMetadata.vcs',
    label: 'Version Control',
    type: 'link',
    showInColumn: 0,
    description: 'Version Control',
  },
  {
    name: 'technicalMetadata.created_by',
    label: 'Created by',
    showInColumn: 0,
    description: 'Created by',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Type',
    showInColumn: 0,
    description: 'Asset type',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Status',
    showInColumn: 0,
    description: 'Asset status',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Technical Description',
    description: 'Technical Description',
  },
  {
    name: 'businessMetadata.administrativeMetadata.owners',
    label: 'Owners',
    type: 'owners',
    showInColumn: 2,
    description: 'Owners',
  },
];

const customQueryForListPage = `
  Harmony {
    harmony_url
    cluster
    datahub_urn
  }
`;

const customQueryForDetailsPage = `
  Harmony {
    bash
    cluster
    datahub_urn
    delay_check_enabled
    delay_threshold
    deleted
    email
    enabled
    end_date
    external_job_id
    failure_check_enabled
    failure_check_time_period
    failure_threshold
    harmony_url
    input_data_jobs
    interval
    job_id
    job_type
    last_sequential_run
    long_running_check_enabled
    long_running_threshold
    mail_list
    max_concurrency
    max_retry
    name
    operator_group
    ordering
    priority
    scan_date
    send_mail
    sla
    start_date_time
    tags {
      name
      value
    }
    task_memory_in_mb
    task_timeout_in_minutes
    timezone
    yellow_pages_service
    yp_service_id
  }
`;

const searchableFields = getSearchableFields(attributesMap);

export const HarmonyConfig: DataSource = {
  id: '5',
  name: 'Harmony',
  showLineage: true,
  showLineageTable: true,
  showLineagePipeline: false,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: false,
  showLineageDashboard: false,
  showColumns: false,
  showPartitionColumns: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields,
};
